/**
 * Token Cache Utility
 * Uses Cognito Identity Service Provider cache entries (CognitoIdentityServiceProvider.*)
 * Tracks expiry time and token type in metadata
 */

import { CognitoUserData } from './getMergedUserData';

export interface CachedTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresAt: number; // Unix timestamp in milliseconds
  idTokenExpiresAt: number;
  accessTokenExpiresAt: number;
}

interface TokenMetadata {
  tokenType: string;
  expiresAt: number;
  idTokenExpiresAt: number;
  accessTokenExpiresAt: number;
}

const TOKEN_METADATA_KEY = 'auth-tokens-metadata'; // Store expiry and token type separately
const TOKEN_TYPE = 'Bearer';

// Get Cognito client ID
const getClientId = (): string | null => {
  return process.env.REACT_APP_COGNITO_CLIENT_ID || null;
};

// Get Cognito prefix for localStorage keys
const getCognitoPrefix = (): string | null => {
  const clientId = getClientId();
  return clientId ? `CognitoIdentityServiceProvider.${clientId}.` : null;
};

// Get current authenticated username from Cognito cache
const getCurrentUsername = (): string | null => {
  try {
    const prefix = getCognitoPrefix();
    if (!prefix) return null;
    
    const lastAuthUserKey = `${prefix}LastAuthUser`;
    return localStorage.getItem(lastAuthUserKey);
  } catch (error) {
    console.error('❌ Failed to get current username from Cognito cache:', error);
    return null;
  }
};

/**
 * Save token metadata (expiry time and token type)
 * Tokens themselves are stored by Cognito/Amplify in CognitoIdentityServiceProvider.* entries
 * @param tokenType - Token type from Cognito response (usually 'Bearer')
 */
export const saveTokensToCache = (
  expiresIn?: number, // Optional expiry in seconds from Cognito response
  tokenType: string = TOKEN_TYPE // Token type from Cognito response
): void => {
  const now = Date.now();
  
  // Default expiry times (1 hour for access/id tokens, 30 days for refresh token)
  const defaultExpiresIn = expiresIn || 3600; // 1 hour in seconds
  const idTokenExpiry = now + (defaultExpiresIn * 1000);
  const accessTokenExpiry = now + (defaultExpiresIn * 1000);
  // Use the earliest expiry as the overall expiry
  const overallExpiry = Math.min(idTokenExpiry, accessTokenExpiry);
  
  // Store metadata (expiry and token type) separately
  // Tokens themselves are stored by Cognito/Amplify
  const metadata: TokenMetadata = {
    tokenType: tokenType,
    expiresAt: overallExpiry,
    idTokenExpiresAt: idTokenExpiry,
    accessTokenExpiresAt: accessTokenExpiry,
  };
  
  try {
    localStorage.setItem(TOKEN_METADATA_KEY, JSON.stringify(metadata));
    console.log('✅ Token metadata saved with expiry:', {
      expiresAt: new Date(overallExpiry).toISOString(),
      idTokenExpiresAt: new Date(idTokenExpiry).toISOString(),
      accessTokenExpiresAt: new Date(accessTokenExpiry).toISOString(),
      tokenType: tokenType
    });
  } catch (error) {
    console.error('❌ Failed to save token metadata:', error);
  }
};

/**
 * Get tokens from Cognito cache entries if they haven't expired
 */
export const getTokensFromCache = (): CachedTokens | null => {
  try {
    const prefix = getCognitoPrefix();
    if (!prefix) {
      return null;
    }
    
    const username = getCurrentUsername();
    if (!username) {
      return null;
    }
    
    // Read tokens from Cognito cache entries
    const idToken = localStorage.getItem(`${prefix}${username}.idToken`);
    const accessToken = localStorage.getItem(`${prefix}${username}.accessToken`);
    const refreshToken = localStorage.getItem(`${prefix}${username}.refreshToken`);
    
    if (!idToken || !accessToken) {
      return null;
    }
    
    // Get metadata (expiry and token type)
    const metadataStr = localStorage.getItem(TOKEN_METADATA_KEY);
    
    // If no metadata exists, try to use tokens anyway with default expiry
    if (!metadataStr) {
      console.log('⚠️ No token metadata found, using tokens with default expiry (1 hour)');
      const now = Date.now();
      const defaultExpiry = now + (3600 * 1000); // 1 hour default
      
      // Format tokens without Bearer prefix (will be added by API calls if needed)
      return {
        idToken: idToken,
        accessToken: accessToken,
        refreshToken: refreshToken || '',
        tokenType: TOKEN_TYPE,
        expiresAt: defaultExpiry,
        idTokenExpiresAt: defaultExpiry,
        accessTokenExpiresAt: defaultExpiry,
      };
    }
    
    const metadata: TokenMetadata = JSON.parse(metadataStr);
    const now = Date.now();
    
    // Check if tokens are expired
    if (now >= metadata.expiresAt) {
      console.log('⚠️ Cached tokens have expired');
      clearTokensFromCache();
      return null;
    }
    
    // Don't add Bearer prefix here - let API calls handle it
    return {
      idToken: idToken,
      accessToken: accessToken,
      refreshToken: refreshToken || '',
      tokenType: metadata.tokenType,
      expiresAt: metadata.expiresAt,
      idTokenExpiresAt: metadata.idTokenExpiresAt,
      accessTokenExpiresAt: metadata.accessTokenExpiresAt,
    };
  } catch (error) {
    console.error('❌ Failed to get tokens from Cognito cache:', error);
    return null;
  }
};

/**
 * Clear token metadata from cache
 * Note: Cognito cache entries are cleared separately via clearCognitoCache()
 */
export const clearTokensFromCache = (): void => {
  try {
    localStorage.removeItem(TOKEN_METADATA_KEY);
    // Also clear user data when tokens are cleared
    clearUserDataFromCache();
    console.log('✅ Token metadata cleared from cache');
  } catch (error) {
    console.error('❌ Failed to clear token metadata:', error);
  }
};

/**
 * Check if tokens are valid (not expired)
 */
export const areTokensValid = (): boolean => {
  const tokens = getTokensFromCache();
  return tokens !== null;
};

/**
 * Get token expiry time remaining in milliseconds
 */
export const getTokenExpiryRemaining = (): number => {
  const tokens = getTokensFromCache();
  if (!tokens) {
    return 0;
  }
  
  const remaining = tokens.expiresAt - Date.now();
  return Math.max(0, remaining);
};

/**
 * Check if tokens need refresh (within 5 minutes of expiry)
 * @param bufferMinutes - Buffer time in minutes before expiry to trigger refresh (default: 5)
 */
export const shouldRefreshTokens = (bufferMinutes: number = 5): boolean => {
  const tokens = getTokensFromCache();
  if (!tokens) {
    return false;
  }
  
  const remaining = tokens.expiresAt - Date.now();
  const bufferMs = bufferMinutes * 60 * 1000;
  
  // Return true if tokens expire within the buffer time
  return remaining > 0 && remaining <= bufferMs;
};

/**
 * Get refresh token from Cognito cache
 */
export const getRefreshToken = (): string | null => {
  try {
    const prefix = getCognitoPrefix();
    if (!prefix) {
      return null;
    }
    
    const username = getCurrentUsername();
    if (!username) {
      return null;
    }
    
    const refreshToken = localStorage.getItem(`${prefix}${username}.refreshToken`);
    return refreshToken;
  } catch (error) {
    console.error('❌ Failed to get refresh token from Cognito cache:', error);
    return null;
  }
};

/**
 * Clear all Cognito Identity Service Provider cache entries
 * These are created automatically by AWS Amplify when using Cognito
 */
export const clearCognitoCache = (): void => {
  try {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    if (!clientId) {
      console.warn('⚠️ Cognito Client ID not found, cannot clear Cognito cache');
      return;
    }

    const cognitoPrefix = `CognitoIdentityServiceProvider.${clientId}.`;
    const keysToRemove: string[] = [];

    // Find all Cognito cache entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(cognitoPrefix)) {
        keysToRemove.push(key);
      }
    }

    // Remove all Cognito entries
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    if (keysToRemove.length > 0) {
      console.log(`✅ Cleared ${keysToRemove.length} Cognito cache entries`);
    }
  } catch (error) {
    console.error('❌ Failed to clear Cognito cache:', error);
  }
};

/**
 * Cognito User Data Cache
 * Stores user data from Cognito with same structure as token cache
 */

interface CachedUserData extends CognitoUserData {
  cachedAt: number; // Unix timestamp in milliseconds
  expiresAt: number; // Unix timestamp in milliseconds (same as token expiry)
}

const USER_DATA_KEY = 'cognito-user-data';

/**
 * Save Cognito user data to cache with expiry (same as token expiry)
 */
export const saveUserDataToCache = (
  userData: CognitoUserData,
  expiresAt: number // Token expiry time
): void => {
  try {
    const cachedData: CachedUserData = {
      ...userData,
      cachedAt: Date.now(),
      expiresAt: expiresAt,
    };
    
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(cachedData));
    console.log('✅ Cognito user data saved to cache with expiry:', {
      expiresAt: new Date(expiresAt).toISOString(),
      userData
    });
  } catch (error) {
    console.error('❌ Failed to save user data to cache:', error);
  }
};

/**
 * Get Cognito user data from cache if not expired
 */
export const getUserDataFromCache = (): CognitoUserData | null => {
  try {
    const cachedDataStr = localStorage.getItem(USER_DATA_KEY);
    if (!cachedDataStr) {
      return null;
    }
    
    const cachedData: CachedUserData = JSON.parse(cachedDataStr);
    const now = Date.now();
    
    // Check if cached data has expired (same expiry as tokens)
    if (now >= cachedData.expiresAt) {
      console.log('⚠️ Cached user data has expired');
      clearUserDataFromCache();
      return null;
    }
    
    // Return user data without cache metadata
    const { cachedAt, expiresAt, ...userData } = cachedData;
    return userData;
  } catch (error) {
    console.error('❌ Failed to get user data from cache:', error);
    return null;
  }
};

/**
 * Clear user data from cache
 */
export const clearUserDataFromCache = (): void => {
  try {
    localStorage.removeItem(USER_DATA_KEY);
    console.log('✅ User data cleared from cache');
  } catch (error) {
    console.error('❌ Failed to clear user data from cache:', error);
  }
};

